// Utility functions for downloading SEO reports

/**
 * Download report as JSON
 * @param {Object} report - The SEO report data
 * @param {string} url - The analyzed URL
 */
export const downloadJSON = (report, url) => {
  const dataStr = JSON.stringify(report, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  
  const link = document.createElement('a');
  link.href = URL.createObjectURL(dataBlob);
  link.download = `seo-report-${url.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
  link.click();
  
  URL.revokeObjectURL(link.href);
};

/**
 * Generate and print a printer-friendly PDF version of the report
 * @param {Object} report - The SEO report data
 * @param {string} url - The analyzed URL
 */
export const printPDF = (report, url) => {
  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  
  // Generate printer-friendly HTML
  const htmlContent = generatePrinterFriendlyHTML(report, url);
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  // Wait for content to load then print
  printWindow.onload = () => {
    printWindow.print();
    // Close the window after printing (optional)
    // printWindow.close();
  };
};

/**
 * Generate printer-friendly HTML content
 * @param {Object} report - The SEO report data
 * @param {string} url - The analyzed URL
 * @returns {string} - HTML content for printing
 */
const generatePrinterFriendlyHTML = (report, url) => {
  const currentDate = new Date().toLocaleDateString();
  const currentTime = new Date().toLocaleTimeString();
  
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>SEO Report - ${url}</title>
      <style>
        @media print {
          body { margin: 0; padding: 20px; }
          .no-print { display: none; }
          .page-break { page-break-before: always; }
        }
        
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }
        
        .header {
          text-align: center;
          border-bottom: 3px solid #2563eb;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        
        .header h1 {
          color: #2563eb;
          margin: 0;
          font-size: 28px;
        }
        
        .header .url {
          font-size: 18px;
          color: #666;
          margin: 10px 0;
        }
        
        .header .date {
          font-size: 14px;
          color: #888;
        }
        
        .score-section {
          text-align: center;
          background: #f8fafc;
          border: 2px solid #2563eb;
          border-radius: 10px;
          padding: 30px;
          margin: 30px 0;
        }
        
        .score-circle {
          display: inline-block;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          color: white;
          text-align: center;
          line-height: 120px;
          font-size: 36px;
          font-weight: bold;
          margin: 20px 0;
        }
        
        .grade {
          font-size: 24px;
          font-weight: bold;
          color: #2563eb;
          margin: 10px 0;
        }
        
        .section {
          margin: 30px 0;
          page-break-inside: avoid;
        }
        
        .section h3 {
          color: #2563eb;
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 10px;
          margin-bottom: 20px;
          font-size: 20px;
        }
        
        .metric {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .metric:last-child {
          border-bottom: none;
        }
        
        .metric-label {
          font-weight: 600;
          color: #374151;
        }
        
        .metric-value {
          color: #6b7280;
        }
        
        .status-pass { color: #059669; }
        .status-fail { color: #dc2626; }
        .status-warning { color: #d97706; }
        
        .recommendations {
          background: #f0f9ff;
          border-left: 4px solid #2563eb;
          padding: 20px;
          margin: 20px 0;
          border-radius: 0 8px 8px 0;
        }
        
        .recommendations h4 {
          color: #1e40af;
          margin-top: 0;
        }
        
        .footer {
          text-align: center;
          margin-top: 50px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
          color: #6b7280;
          font-size: 14px;
        }
        
        @media print {
          .page-break { page-break-before: always; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>SEO Analysis Report</h1>
        <div class="url">${url}</div>
        <div class="date">Generated on ${currentDate} at ${currentTime}</div>
      </div>
      
      <div class="score-section">
        <h2>Overall SEO Score</h2>
        <div class="score-circle">${report.score}</div>
        <div class="grade">Grade: ${report.grade}</div>
        <p>${getScoreDescription(report.score)}</p>
      </div>
      
      <div class="section">
        <h3>Meta Tags Analysis</h3>
        ${renderMetaTagsSection(report.checks.metaTags)}
      </div>
      
      <div class="section">
        <h3>Headings Structure</h3>
        ${renderHeadingsSection(report.checks.headings)}
      </div>
      
      <div class="section">
        <h3>Images & Alt Tags</h3>
        ${renderImagesSection(report.checks.images)}
      </div>
      
      <div class="section">
        <h3>Structured Data</h3>
        ${renderStructuredDataSection(report.checks.structuredData)}
      </div>
      
      <div class="section">
        <h3>Sitemap & Navigation</h3>
        ${renderSitemapSection(report.checks.sitemap)}
      </div>
      
      <div class="section">
        <h3>Performance Metrics</h3>
        ${renderPerformanceSection(report.checks.performance)}
      </div>
      
      <div class="section">
        <h3>Accessibility</h3>
        ${renderAccessibilitySection(report.checks.accessibility)}
      </div>
      
      <div class="section">
        <h3>Content Quality</h3>
        ${renderContentQualitySection(report.checks.contentQuality)}
      </div>
      
      ${report.aiRecommendations ? `
        <div class="section">
          <h3>AI Recommendations</h3>
          <div class="recommendations">
            <h4>Key Improvement Areas</h4>
            ${renderAIRecommendations(report.aiRecommendations)}
          </div>
        </div>
      ` : ''}
      
      ${report.competitiveInsights ? `
        <div class="section">
          <h3>Competitive Analysis</h3>
          <div class="recommendations">
            ${renderCompetitiveInsights(report.competitiveInsights)}
          </div>
        </div>
      ` : ''}
      
      ${report.technicalMetrics ? `
        <div class="section">
          <h3>Technical Metrics</h3>
          ${renderTechnicalMetrics(report.technicalMetrics)}
        </div>
      ` : ''}
      
      <div class="footer">
        <p>Report generated by OneClickAIseo.com</p>
        <p>For detailed analysis and ongoing monitoring, visit our platform</p>
      </div>
    </body>
    </html>
  `;
};

// Helper functions for rendering sections
const getScoreDescription = (score) => {
  if (score >= 80) return "Excellent! Your website is well-optimized for SEO.";
  if (score >= 60) return "Good! There's room for improvement in several areas.";
  return "Needs work! Focus on the recommendations below to improve your SEO.";
};

const renderMetaTagsSection = (metaTags) => {
  if (!metaTags) return '<p>No meta tags data available</p>';
  
  const items = [];
  if (metaTags.title) items.push({ label: 'Title Tag', value: metaTags.title, status: 'pass' });
  if (metaTags.description) items.push({ label: 'Meta Description', value: metaTags.description, status: 'pass' });
  if (metaTags.keywords) items.push({ label: 'Meta Keywords', value: metaTags.keywords, status: 'pass' });
  if (metaTags.viewport) items.push({ label: 'Viewport Meta', value: metaTags.viewport, status: 'pass' });
  
  return items.map(item => `
    <div class="metric">
      <span class="metric-label">${item.label}</span>
      <span class="metric-value status-${item.status}">✓ Present</span>
    </div>
  `).join('');
};

const renderHeadingsSection = (headings) => {
  if (!headings) return '<p>No headings data available</p>';
  
  let html = '';
  if (headings.h1Count !== undefined) {
    html += `<div class="metric"><span class="metric-label">H1 Tags</span><span class="metric-value">${headings.h1Count}</span></div>`;
  }
  if (headings.h2Count !== undefined) {
    html += `<div class="metric"><span class="metric-label">H2 Tags</span><span class="metric-value">${headings.h2Count}</span></div>`;
  }
  if (headings.structure) {
    html += `<div class="metric"><span class="metric-label">Structure</span><span class="metric-value">${headings.structure}</span></div>`;
  }
  return html || '<p>No headings data available</p>';
};

const renderImagesSection = (images) => {
  if (!images) return '<p>No images data available</p>';
  
  let html = '';
  if (images.totalImages !== undefined) {
    html += `<div class="metric"><span class="metric-label">Total Images</span><span class="metric-value">${images.totalImages}</span></div>`;
  }
  if (images.imagesWithAlt !== undefined) {
    html += `<div class="metric"><span class="metric-label">Images with Alt Tags</span><span class="metric-value">${images.imagesWithAlt}</span></div>`;
  }
  if (images.altTagPercentage !== undefined) {
    html += `<div class="metric"><span class="metric-label">Alt Tag Coverage</span><span class="metric-value">${images.altTagPercentage}%</span></div>`;
  }
  return html || '<p>No images data available</p>';
};

const renderStructuredDataSection = (structuredData) => {
  if (!structuredData) return '<p>No structured data available</p>';
  
  let html = '';
  if (structuredData.schemaCount !== undefined) {
    html += `<div class="metric"><span class="metric-label">Schema Markup</span><span class="metric-value">${structuredData.schemaCount} found</span></div>`;
  }
  if (structuredData.jsonLdCount !== undefined) {
    html += `<div class="metric"><span class="metric-label">JSON-LD Scripts</span><span class="metric-value">${structuredData.jsonLdCount} found</span></div>`;
  }
  if (structuredData.microdataCount !== undefined) {
    html += `<div class="metric"><span class="metric-label">Microdata</span><span class="metric-value">${structuredData.microdataCount} found</span></div>`;
  }
  return html || '<p>No structured data available</p>';
};

const renderSitemapSection = (sitemap) => {
  if (!sitemap) return '<p>No sitemap data available</p>';
  
  let html = '';
  if (sitemap.sitemapUrl) {
    html += `<div class="metric"><span class="metric-label">Sitemap URL</span><span class="metric-value">${sitemap.sitemapUrl}</span></div>`;
  }
  if (sitemap.sitemapSize !== undefined) {
    html += `<div class="metric"><span class="metric-label">Sitemap Size</span><span class="metric-value">${sitemap.sitemapSize} URLs</span></div>`;
  }
  if (sitemap.robotsTxt) {
    html += `<div class="metric"><span class="metric-label">Robots.txt</span><span class="metric-value">✓ Present</span></div>`;
  }
  return html || '<p>No sitemap data available</p>';
};

const renderPerformanceSection = (performance) => {
  if (!performance) return '<p>No performance data available</p>';
  
  let html = '';
  if (performance.loadTime !== undefined) {
    html += `<div class="metric"><span class="metric-label">Load Time</span><span class="metric-value">${performance.loadTime}ms</span></div>`;
  }
  if (performance.pageSize !== undefined) {
    html += `<div class="metric"><span class="metric-label">Page Size</span><span class="metric-value">${Math.round(performance.pageSize / 1024)} KB</span></div>`;
  }
  if (performance.score !== undefined) {
    html += `<div class="metric"><span class="metric-label">Performance Score</span><span class="metric-value">${performance.score}/15</span></div>`;
  }
  return html || '<p>No performance data available</p>';
};

const renderAccessibilitySection = (accessibility) => {
  if (!accessibility) return '<p>No accessibility data available</p>';
  
  let html = '';
  if (accessibility.ariaLabels !== undefined) {
    html += `<div class="metric"><span class="metric-label">ARIA Labels</span><span class="metric-value">${accessibility.ariaLabels} found</span></div>`;
  }
  if (accessibility.contrastRatio !== undefined) {
    html += `<div class="metric"><span class="metric-label">Contrast Ratio</span><span class="metric-value">${accessibility.contrastRatio}</span></div>`;
  }
  if (accessibility.score !== undefined) {
    html += `<div class="metric"><span class="metric-label">Accessibility Score</span><span class="metric-value">${accessibility.score}/15</span></div>`;
  }
  return html || '<p>No accessibility data available</p>';
};

const renderContentQualitySection = (contentQuality) => {
  if (!contentQuality) return '<p>No content quality data available</p>';
  
  let html = '';
  if (contentQuality.wordCount !== undefined) {
    html += `<div class="metric"><span class="metric-label">Word Count</span><span class="metric-value">${contentQuality.wordCount}</span></div>`;
  }
  if (contentQuality.readabilityScore !== undefined) {
    html += `<div class="metric"><span class="metric-label">Readability Score</span><span class="metric-value">${contentQuality.readabilityScore}</span></div>`;
  }
  if (contentQuality.score !== undefined) {
    html += `<div class="metric"><span class="metric-label">Content Quality Score</span><span class="metric-value">${contentQuality.score}/15</span></div>`;
  }
  return html || '<p>No content quality data available</p>';
};

const renderAIRecommendations = (recommendations) => {
  if (!recommendations) return '<p>No AI recommendations available</p>';
  
  if (typeof recommendations === 'string') {
    return `<p>${recommendations}</p>`;
  }
  
  if (Array.isArray(recommendations)) {
    return recommendations.map(rec => `<p>• ${rec}</p>`).join('');
  }
  
  return '<p>No AI recommendations available</p>';
};

const renderCompetitiveInsights = (insights) => {
  if (!insights) return '<p>No competitive insights available</p>';
  
  if (insights.analysis && typeof insights.analysis === 'string') {
    return `<p>${insights.analysis}</p>`;
  }
  
  return '<p>No competitive insights available</p>';
};

const renderTechnicalMetrics = (metrics) => {
  if (!metrics) return '<p>No technical metrics available</p>';
  
  let html = '';
  if (metrics.pageSize !== undefined) {
    html += `<div class="metric"><span class="metric-label">Page Size</span><span class="metric-value">${Math.round(metrics.pageSize / 1024)} KB</span></div>`;
  }
  if (metrics.compression !== undefined) {
    html += `<div class="metric"><span class="metric-label">Compression</span><span class="metric-value">${metrics.compression ? 'Yes' : 'No'}</span></div>`;
  }
  if (metrics.cacheControl !== undefined) {
    html += `<div class="metric"><span class="metric-label">Cache Control</span><span class="metric-value">${metrics.cacheControl ? 'Yes' : 'No'}</span></div>`;
  }
  if (metrics.score !== undefined) {
    html += `<div class="metric"><span class="metric-label">Technical Score</span><span class="metric-value">${metrics.score}/15</span></div>`;
  }
  return html || '<p>No technical metrics available</p>';
};
